const viteEnv =
  typeof import.meta !== 'undefined' && typeof import.meta.env !== 'undefined'
    ? import.meta.env
    : ({} as Record<string, string | undefined>)

function resolveLocalMainSiteUrl() {
  if (typeof window === 'undefined') return ''
  const { hostname, protocol } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:4010`
  }
  return window.location.origin
}

const apiBaseUrl = (viteEnv.VITE_AUTH_API_BASE_URL ?? viteEnv.VITE_MAIN_SITE_URL ?? resolveLocalMainSiteUrl()).replace(/\/+$/, '')

const VIDEO_GEN_ENDPOINT = apiBaseUrl ? `${apiBaseUrl}/api/subsite/video-gen/aliyun` : '/api/subsite/video-gen/aliyun'

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

export type VideoMode = 't2v' | 'i2v' | 'r2v'
export type VideoResolution = '720P' | '1080P'
export type VideoRatio = '16:9' | '9:16' | '1:1' | '4:3' | '3:4' | '4:5' | '5:4' | '9:21' | '21:9'
export type VideoFamily = 'wan' | 'happyhorse'

export type VideoModelOption = {
  value: string
  label: string
  mode: VideoMode
  family: VideoFamily
  /** 模型族显示名 */
  familyLabel: string
  /** 模型族介绍 */
  familyDescription: string
  description: string
  /** 720P 每秒模型成本（元），仅用于界面预估；服务端为最终扣点依据 */
  pricePerSec: number
  /** 1080P 每秒模型成本（元） */
  pricePerSec1080: number
  /** 是否支持负面提示词 */
  supportsNegativePrompt: boolean
  /** 是否支持提示词智能改写 */
  supportsPromptExtend: boolean
  /** 是否有声视频 */
  hasAudio: boolean
  /** 最小时长 */
  minDuration: number
  /** 最大时长 */
  maxDuration: number
}

export const VIDEO_MODEL_OPTIONS: readonly VideoModelOption[] = [
  // 万相 2.7 —— 通义万相产品线，基于图像的视频续写/编辑，支持负面提示词与智能改写
  {
    value: 'wan2.7-t2v',
    label: '万相2.7 文生视频',
    mode: 't2v',
    family: 'wan',
    familyLabel: '万相 2.7 千人千面 智能改写',
    familyDescription:
      '通义万相产品线，侧重高清图像生成及基于图像的短动效视频。支持负面提示词、智能改写、自动配乐音效，适合电商海报、广告素材等对细节控制要求高的场景。',
    description: '文字生成视频，支持自动配乐音效、负面提示词、智能改写。',
    pricePerSec: 0.6,
    pricePerSec1080: 1,
    supportsNegativePrompt: true,
    supportsPromptExtend: true,
    hasAudio: true,
    minDuration: 2,
    maxDuration: 15,
  },
  {
    value: 'wan2.7-i2v',
    label: '万相2.7 图生视频',
    mode: 'i2v',
    family: 'wan',
    familyLabel: '万相 2.7 千人千面 智能改写',
    familyDescription:
      '通义万相产品线，侧重高清图像生成及基于图像的短动效视频。支持负面提示词、智能改写、自动配乐音效，适合电商海报、广告素材等对细节控制要求高的场景。',
    description: '首帧图片生成视频，让画面动起来。',
    pricePerSec: 0.6,
    pricePerSec1080: 1,
    supportsNegativePrompt: true,
    supportsPromptExtend: true,
    hasAudio: true,
    minDuration: 2,
    maxDuration: 15,
  },
  {
    value: 'wan2.7-r2v',
    label: '万相2.7 参考生视频',
    mode: 'r2v',
    family: 'wan',
    familyLabel: '万相 2.7 千人千面 智能改写',
    familyDescription:
      '通义万相产品线，侧重高清图像生成及基于图像的短动效视频。支持负面提示词、智能改写、自动配乐音效，适合电商海报、广告素材等对细节控制要求高的场景。',
    description: '参考图片生成角色一致性视频，支持多图参考。',
    pricePerSec: 0.6,
    pricePerSec1080: 1,
    supportsNegativePrompt: true,
    supportsPromptExtend: true,
    hasAudio: true,
    minDuration: 2,
    maxDuration: 15,
  },
  // 快乐马 1.1 —— 阿里独立端到端视频大模型，主打高真实感动态视频与原生音频同步
  {
    value: 'happyhorse-1.1-t2v',
    label: '快乐马1.1 文生视频',
    mode: 't2v',
    family: 'happyhorse',
    familyLabel: '快乐马 1.1 专业真实 丝滑流畅',
    familyDescription:
      '阿里独立研发的端到端视频生成大模型，专注高真实感动态视频与原生音频同步。物理运动真实、多镜头连贯，适合短剧制作、品牌宣传片等对动态表现力要求高的场景。',
    description: '文字生成视频，物理运动真实流畅，比例选项更丰富。',
    pricePerSec: 0.9,
    pricePerSec1080: 1.2,
    supportsNegativePrompt: false,
    supportsPromptExtend: false,
    hasAudio: false,
    minDuration: 3,
    maxDuration: 15,
  },
  {
    value: 'happyhorse-1.1-i2v',
    label: '快乐马1.1 图生视频',
    mode: 'i2v',
    family: 'happyhorse',
    familyLabel: '快乐马 1.1 专业真实流畅',
    familyDescription:
      '阿里独立研发的端到端视频生成大模型，专注高真实感动态视频与原生音频同步。物理运动真实、多镜头连贯，适合短剧制作、品牌宣传片等对动态表现力要求高的场景。',
    description: '首帧图片生成视频，物理运动自然流畅。',
    pricePerSec: 0.9,
    pricePerSec1080: 1.2,
    supportsNegativePrompt: false,
    supportsPromptExtend: false,
    hasAudio: false,
    minDuration: 3,
    maxDuration: 15,
  },
  {
    value: 'happyhorse-1.1-r2v',
    label: '快乐马1.1 参考生视频',
    mode: 'r2v',
    family: 'happyhorse',
    familyLabel: '快乐马 1.1 专业真实流畅',
    familyDescription:
      '阿里独立研发的端到端视频生成大模型，专注高真实感动态视频与原生音频同步。物理运动真实、多镜头连贯，适合短剧制作、品牌宣传片等对动态表现力要求高的场景。',
    description: '参考图片生成视频，支持多图参考。',
    pricePerSec: 0.9,
    pricePerSec1080: 1.2,
    supportsNegativePrompt: false,
    supportsPromptExtend: false,
    hasAudio: false,
    minDuration: 3,
    maxDuration: 15,
  },
] as const

export const RESOLUTION_OPTIONS: ReadonlyArray<{ value: VideoResolution; label: string }> = [
  { value: '720P', label: '720P' },
  { value: '1080P', label: '1080P' },
] as const

export const RATIO_OPTIONS: ReadonlyArray<{ value: VideoRatio; label: string }> = [
  { value: '16:9', label: '16:9 横屏' },
  { value: '9:16', label: '9:16 竖屏' },
  { value: '1:1', label: '1:1 正方形' },
  { value: '4:3', label: '4:3 横版' },
  { value: '3:4', label: '3:4 竖版' },
  { value: '4:5', label: '4:5' },
  { value: '5:4', label: '5:4' },
  { value: '9:21', label: '9:21 竖长屏' },
  { value: '21:9', label: '21:9 超宽屏' },
] as const

export function getVideoModelOption(value: string) {
  return VIDEO_MODEL_OPTIONS.find((option) => option.value === value)
}

/** 与服务端视频点数规则保持一致；最终预占和扣点仍由服务端计算。 */
export function getEstimatedVideoCredits(
  model: VideoModelOption | undefined,
  resolution: VideoResolution,
  duration: number,
) {
  if (!model) return 0
  const costFen = Math.round((resolution === '1080P' ? model.pricePerSec1080 : model.pricePerSec) * duration * 100)
  return Math.ceil((costFen * 1.5) / 141)
}

function createOutputFileName(model: string) {
  const safeModel = model.toLowerCase().replace(/[^a-z0-9]+/g, '-')
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
  return `ai-video-${safeModel}-${timestamp}.mp4`
}

/** 将 File 转为 base64 字符串（不含 Data URL 前缀） */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('图片文件读取失败。'))
        return
      }
      const base64 = result.includes(',') ? (result.split(',')[1] ?? result) : result
      resolve(base64)
    }
    reader.onerror = () => reject(new Error('图片文件读取失败。'))
    reader.readAsDataURL(file)
  })
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class AliyunVideoService {
  async generateVideo({
    model,
    prompt,
    negativePrompt,
    imageFiles,
    resolution,
    ratio,
    duration,
    seed,
    promptExtend,
    onProgress,
  }: {
    model: string
    prompt: string
    negativePrompt?: string
    imageFiles?: File[]
    resolution?: VideoResolution
    ratio?: VideoRatio
    duration?: number
    seed?: number
    promptExtend?: boolean
    onProgress?: (stage: string) => void
  }): Promise<{ blob: Blob; file: File; objectUrl: string }> {
    const trimmedPrompt = prompt.trim()
    if (!trimmedPrompt) {
      throw new Error('请输入视频描述。')
    }

    onProgress?.('正在提交视频生成任务...')

    const body: Record<string, unknown> = {
      model,
      prompt: trimmedPrompt,
    }
    if (negativePrompt && negativePrompt.trim()) {
      body.negativePrompt = negativePrompt.trim()
    }
    if (imageFiles && imageFiles.length > 0) {
      body.images = await Promise.all(
        imageFiles.map(async (file) => ({
          base64: await fileToBase64(file),
          mimeType: (file.type || 'image/jpeg') as 'image/jpeg' | 'image/png' | 'image/webp' | 'image/bmp',
        })),
      )
    }
    if (resolution) {
      body.resolution = resolution
    }
    if (ratio) {
      body.ratio = ratio
    }
    if (duration) {
      body.duration = duration
    }
    if (seed !== undefined) {
      body.seed = seed
    }
    if (promptExtend !== undefined) {
      body.promptExtend = promptExtend
    }

    const response = await fetch(VIDEO_GEN_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      let message = 'AI 视频生成请求失败。'
      try {
        const payload = (await response.json()) as { message?: string; error?: string }
        message = payload.message || payload.error || message
      } catch {
        // ignore
      }
      throw new Error(message)
    }

    onProgress?.('正在生成视频，预计需要 1-5 分钟...')
    const blob = await response.blob()
    if (blob.size === 0) {
      throw new Error('AI 视频生成没有返回视频数据。')
    }

    const mimeType = blob.type.startsWith('video/') ? blob.type : 'video/mp4'
    const file = new File([blob], createOutputFileName(model), {
      type: mimeType,
      lastModified: Date.now(),
    })
    const objectUrl = URL.createObjectURL(blob)

    return { blob, file, objectUrl }
  }
}

export const aliyunVideoService = new AliyunVideoService()
